import * as React from 'react'

import LocationIcon from '~/client/src/shared/components/LocationIcon/LocationIcon'
import { useTagsStore } from '~/client/src/shared/hooks/useTagsStore'
import { getResourceFieldDefinition } from '~/client/src/shared/resources/definitions'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'

import {
  getResourceLinkTarget,
  IResourceGroupPresentation,
  IResourceLinkConfiguration,
} from '../resourceLink'

export interface IResolveGroupPresentationArgs {
  propertyConfig: IResourceLinkConfiguration | undefined
  groupKey: string
  count?: number
  /**
   * The field being grouped by, and the type it belongs to. BOTH are required to identify
   * a field: Equipment's taxonomy property is also named `type`, so a name-only test would
   * paint location glyphs on Equipment's type groups.
   */
  propertyName?: string
  resourceTypeId?: string
}

/**
 * The glyph a field's own definition asks for on a group header, or null.
 *
 * Kept behind the registered link targets, which own the richer presentation (a linked
 * record contributes its label and colour too). A field declaring `table.groupIconName`
 * contributes artwork only.
 */
function resolveFieldGroupIcon(
  args: IResolveGroupPresentationArgs,
): IResourceGroupPresentation | null {
  const { resourceTypeId, propertyName, groupKey } = args
  if (!resourceTypeId || !propertyName || !groupKey) return null

  const iconName = getResourceFieldDefinition(
    resourceTypeId,
    propertyName,
  )?.table?.groupIconName?.(groupKey)

  return iconName ? { labelIcon: <LocationIcon iconName={iconName} /> } : null
}

export function resolveGroupPresentation(
  args: IResolveGroupPresentationArgs,
  tagsStore: TagsStore,
): IResourceGroupPresentation | null {
  // Link targets first: they were here before this fallback and must stay unconditional.
  const target = getResourceLinkTarget(args.propertyConfig)
  if (target?.resolveGroupPresentation) {
    return target.resolveGroupPresentation(
      { groupKey: args.groupKey, count: args.count },
      { tagsStore },
    )
  }

  return resolveFieldGroupIcon(args)
}

export function useResolveGroupPresentation(): (
  args: IResolveGroupPresentationArgs,
) => IResourceGroupPresentation | null {
  const tagsStore = useTagsStore()
  return React.useCallback(
    args => resolveGroupPresentation(args, tagsStore),
    [tagsStore],
  )
}
